import { PackageIcon } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import useSWR from 'swr'

import {
  PreferenceCategory,
  PreferenceInfo,
  PreferenceInput,
  PreferenceLink,
  PreferenceScreen,
} from '../../components/Preference'
import { fetcher } from '../../lib/swr'
import type { CogoMappedArticleDraft } from './cogoProductMap'

export type CogoProductMapListItem = {
  productId: number
  productDisplay: string
  productUnit: string
  articles: CogoMappedArticleDraft[]
  chosenAt: string | null
}

export type CogoUnmappedProductListItem = {
  productId: number
  productDisplay: string
  productUnit: string
}

type MapsResponse = {
  maps: CogoProductMapListItem[]
  unmapped?: CogoUnmappedProductListItem[]
}

function articleSummary(item: CogoProductMapListItem): string {
  const n = item.articles.length
  const count = `${n} artikel${n === 1 ? '' : 'en'}`
  const unit = item.productUnit ? ` · ${item.productUnit}` : ''

  if (n) {
    return item.articles
      .map((a) => a.display)
      .filter((s): s is string => Boolean(s))
      .join('\n')
  }
  return `${count}${unit}`
}

export default function CogoProductsSettings() {
  const maps = useSWR<MapsResponse>('/api/cogo/maps', fetcher)
  const rows = maps.data?.maps ?? []
  const unmappedRows = maps.data?.unmapped ?? []
  const [q, setQ] = useState('')

  useEffect(() => {
    if (maps.error) {
      toast.error(maps.error.message || 'Kon product-maps niet laden')
    }
  }, [maps.error])

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase()
    if (!term) return rows
    return rows.filter((row) => {
      if (row.productDisplay.toLowerCase().includes(term)) return true
      return row.articles.some(
        (a) =>
          a.display.toLowerCase().includes(term) ||
          a.technicalProductId.toLowerCase().includes(term)
      )
    })
  }, [rows, q])

  const filteredUnmapped = useMemo(() => {
    const term = q.trim().toLowerCase()
    if (!term) return unmappedRows
    return unmappedRows.filter((row) =>
      row.productDisplay.toLowerCase().includes(term)
    )
  }, [unmappedRows, q])

  return (
    <PreferenceScreen title="Producten" backHref="/settings/cogo">
      <PreferenceCategory filled>
        <PreferenceInput
          title=""
          value={q}
          onChange={setQ}
          placeholder="Zoeken op product of artikel..."
        />
      </PreferenceCategory>

      {/* mapped */}
      <PreferenceCategory
        title={
          maps.isLoading
            ? 'Laden…'
            : filtered.length === rows.length
              ? `${rows.length} gekoppeld`
              : `${filtered.length} van ${rows.length}`
        }
        filled
      >
        {maps.isLoading && rows.length === 0 ? (
          <PreferenceInfo title="Laden…" />
        ) : filtered.length === 0 ? (
          <PreferenceInfo
            icon={<PackageIcon className="h-6 w-6" />}
            title={rows.length === 0 ? 'Nog geen koppelingen' : 'Geen treffers'}
            summary={
              rows.length === 0
                ? 'Koppelingen ontstaan wanneer je bij sync een artikel kiest.'
                : 'Probeer een andere zoekterm.'
            }
          />
        ) : (
          filtered.map((row) => (
            <PreferenceLink
              key={row.productId}
              title={row.productDisplay}
              summary={articleSummary(row)}
              href={`/settings/cogo/products/${row.productId}`}
            />
          ))
        )}
      </PreferenceCategory>

      {/* unmapped — shopping-list order */}
      <PreferenceCategory
        title={
          maps.isLoading
            ? 'Te koppelen'
            : filteredUnmapped.length === unmappedRows.length
              ? unmappedRows.length === 0
                ? 'Te koppelen'
                : `${unmappedRows.length} te koppelen`
              : `${filteredUnmapped.length} van ${unmappedRows.length}`
        }
        filled
      >
        {maps.isLoading && unmappedRows.length === 0 ? (
          <PreferenceInfo title="Laden…" />
        ) : filteredUnmapped.length === 0 ? (
          <PreferenceInfo
            icon={<PackageIcon className="h-6 w-6" />}
            title={
              unmappedRows.length === 0 ? 'Alles is gekoppeld' : 'Geen treffers'
            }
            summary={
              unmappedRows.length === 0
                ? 'Geen ongekoppelde producten op open boodschappenlijsten.'
                : 'Probeer een andere zoekterm.'
            }
          />
        ) : (
          filteredUnmapped.map((row) => (
            <PreferenceLink
              key={row.productId}
              title={row.productDisplay}
              summary={row.productUnit || undefined}
              href={`/settings/cogo/products/${row.productId}`}
            />
          ))
        )}
      </PreferenceCategory>
    </PreferenceScreen>
  )
}
